import { makeQueryPlanMetadataId, makeQueryWithCalcsPlanMetadataId } from '@mode/shared/util-js';
import { PlanMetadataActions, PlanPaging } from '@mode/switch-views/contract';
import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { ActionReducerMap, createReducer, on } from '@ngrx/store';

// @TODO: should this definition stay here or be moved to one of the common types modules?
export interface PlanMetadataFeatureState {
  paging: PlanPagingState;
}

export const planMetadataFeatureKey = 'planMetadata';

export interface PlanMetadataRoot {
  [planMetadataFeatureKey]: PlanMetadataFeatureState;
}

// Paging
export interface PlanPagingState extends EntityState<PlanPaging> {}
const planPagingAdapter: EntityAdapter<PlanPaging> = createEntityAdapter<PlanPaging>({
  selectId: (model) => model.planMetadataId,
});

export const initialState: PlanMetadataFeatureState = {
  paging: planPagingAdapter.getInitialState(),
};

export const pagingReducer = createReducer(
  initialState.paging,

  on(PlanMetadataActions.initPaging, (state, action) => {
    // This is intentionally using `setOne` to add or replace if a value already exists.
    return planPagingAdapter.setOne(
      {
        planMetadataId: action.planMetadataId,
        offset: action.offset,
        pageSize: action.pageSize,
      },
      state
    );
  }),

  on(PlanMetadataActions.initPagingIfUnset, (state, action) => {
    // This is intentionally using `addOne` so an entity is only added if it doesn't yet exist.
    return planPagingAdapter.addOne(
      {
        planMetadataId: action.planMetadataId,
        offset: action.offset,
        pageSize: action.pageSize,
      },
      state
    );
  }),

  on(PlanMetadataActions.setOffsetInDesigner, (state, action) => {
    // update here as this only happens after a paging item has been initialized
    return planPagingAdapter.updateOne(
      {
        id: action.planMetadataId,
        changes: {
          offset: action.offset,
        },
      },
      state
    );
  }),

  on(PlanMetadataActions.setOffsetRawResults, (state, action) => {
    // update here as this only happens after a paging item has been initialized
    const planMetadataId = makeQueryPlanMetadataId(action.queryToken);
    return planPagingAdapter.updateOne(
      {
        id: planMetadataId,
        changes: {
          offset: action.offset,
        },
      },
      state
    );
  }),

  on(PlanMetadataActions.setOffsetWithCalcs, (state, action) => {
    // update here as this only happens after a paging item has been initialized
    const planMetadataId = makeQueryWithCalcsPlanMetadataId(action.queryToken);
    return planPagingAdapter.updateOne(
      {
        id: planMetadataId,
        changes: {
          offset: action.offset,
        },
      },
      state
    );
  }),

  on(PlanMetadataActions.setPageSizeInDesigner, (state, action) => {
    return planPagingAdapter.updateOne(
      {
        id: action.planMetadataId,
        changes: {
          pageSize: action.pageSize,
          offset: 0,
        },
      },
      state
    );
  }),

  on(PlanMetadataActions.resetOffsetInDesigner, (state, action) => {
    return planPagingAdapter.updateOne(
      {
        id: action.planMetadataId,
        changes: {
          offset: 0,
        },
      },
      state
    );
  })
);

const reducer: ActionReducerMap<PlanMetadataFeatureState> = {
  paging: pagingReducer,
};

export const {
  selectAll: planPagingSelectAll,
  selectEntities: planPagingSelectEntities,
  selectIds: planPagingSelectIds,
  selectTotal: planPagingSelectTotal,
} = planPagingAdapter.getSelectors();

export { planPagingAdapter, reducer };
